import { StateCreator } from 'zustand';
import { DataSlice, KharonStore } from '../types';
import { Client, DeceasedClient } from '../../types';
import { 
  getFirestoreCases, 
  getFirestoreClients, 
  getFirestoreDeceased,
  getFirestoreServices,
  getFirestorePlans,
  getFirestoreTemplates,
  addFirestoreTemplate,
  deleteFirestoreTemplate,
  getFirestoreSettings,
  updateFirestoreSettings,
  addFirestoreCase,
  addFirestoreClient,
  addFirestoreDeceased,
  addFirestoreService,
  addFirestorePlan,
  deleteFirestoreService,
  deleteFirestorePlan,
  deleteFirestoreClient,
  deleteFirestoreDeceased,
  getFirestoreUsers,
  saveFirestoreUser
} from '../../api/firestore';
import { db } from '../../lib/firebase';
import { doc, updateDoc, writeBatch } from 'firebase/firestore';
import { getDefaultTemplates } from '../../lib/defaultTemplates';

export const createDataSlice: StateCreator<
  KharonStore,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  DataSlice
> = (set, get) => ({
  appUsers: [],
  clients: [],
  deceasedClients: [],
  cases: [],
  individualServices: [],
  servicePlans: [],
  documentTemplates: [],
  settings: null,
  lastUpdated: {},
  serviceDraft: null,

  setAppUsers: (users) => set((state) => { state.appUsers = users; state.updateTimestamp('appUsers'); }, false, 'data/setAppUsers'),
  setClients: (clients) => set((state) => { state.clients = clients; state.updateTimestamp('clients'); }, false, 'data/setClients'),
  setDeceasedClients: (deceased) => set((state) => { state.deceasedClients = deceased; state.updateTimestamp('deceasedClients'); }, false, 'data/setDeceasedClients'),
  setCases: (cases) => set((state) => { state.cases = cases; state.updateTimestamp('cases'); }, false, 'data/setCases'),
  setIndividualServices: (services) => set((state) => { state.individualServices = services; state.updateTimestamp('individualServices'); }, false, 'data/setIndividualServices'),
  setServicePlans: (plans) => set((state) => { state.servicePlans = plans; state.updateTimestamp('servicePlans'); }, false, 'data/setServicePlans'),
  setDocumentTemplates: (templates) => set((state) => { state.documentTemplates = templates; state.updateTimestamp('documentTemplates'); }, false, 'data/setDocumentTemplates'),
  setSettings: (settings) => set((state) => { state.settings = settings; state.updateTimestamp('settings'); }, false, 'data/setSettings'),
  setServiceDraft: (draft) => set((state) => { state.serviceDraft = draft; }, false, 'data/setServiceDraft'),
  updateTimestamp: (entity) => set((state) => { state.lastUpdated[entity] = Date.now(); }, false, 'data/updateTimestamp'),

  fetchAllData: async () => {
    set((state) => { 
      state.setIsLoading(true);
      state.setError(null);
    }, false, 'data/fetchAllData/start');

    try {
      const funeralHomeId = get().currentUser?.funeralHomeId;
      if (!funeralHomeId) {
        set((state) => { state.setIsLoading(false); }, false, 'data/fetchAllData/no-fh');
        return;
      }

      const [
        casesData,
        servicesData,
        plansData,
        templatesData,
        clientsData,
        deceasedData,
        settingsData,
        usersData
      ] = await Promise.all([
        getFirestoreCases(funeralHomeId),
        getFirestoreServices(funeralHomeId),
        getFirestorePlans(funeralHomeId),
        getFirestoreTemplates(funeralHomeId),
        getFirestoreClients(funeralHomeId),
        getFirestoreDeceased(funeralHomeId),
        getFirestoreSettings(funeralHomeId),
        getFirestoreUsers(funeralHomeId)
      ]);

      set((state) => {
        state.cases = casesData;
        state.individualServices = servicesData;
        state.servicePlans = plansData;
        state.documentTemplates = templatesData;
        state.clients = clientsData;
        state.deceasedClients = deceasedData;
        state.appUsers = usersData;
        state.settings = settingsData ? {
          funeralHomeInfo: settingsData.funeralHomeInfo || settingsData,
          notificationSettings: settingsData.notificationSettings || { enabled: true, daysInAdvance: 1 }
        } : null;
        state.lastUpdated['_all'] = Date.now();
        state.setIsLoading(false);
      }, false, 'data/fetchAllData/success');
    } catch (error: any) {
      console.error("Error en Firebase Sync:", error);
      set((state) => {
        state.setError("Sincronización con Firebase fallida");
        state.setIsLoading(false);
      }, false, 'data/fetchAllData/error');
    }
  },

  saveCase: async (caseData) => {
    set((state) => { state.setIsLoading(true); state.setError(null); }, false, 'data/saveCase/start');
    try {
      const funeralHomeId = get().currentUser?.funeralHomeId;
      if (!funeralHomeId) throw new Error("No hay funeraria asociada");

      let deceasedId = caseData.deceasedId || caseData.deceased?.id;
      if (caseData.deceased) {
          const fullName = caseData.deceased.fullName || `${caseData.deceased.nombre || ''} ${caseData.deceased.apellidos || ''}`.trim();
          const deceasedDoc = await addFirestoreDeceased({
              ...caseData.deceased,
              fullName: fullName || '',
              deathDate: caseData.deceased.deathDate || caseData.deceased.fechaDefuncion || '',
              birthDate: caseData.deceased.birthDate || caseData.deceased.fechaNacimiento || '',
              dni: (caseData.deceased as any).dni || (caseData.deceased as any).dniNumero || ''
          }, funeralHomeId);
          deceasedId = deceasedDoc.id;
      }

      let clientId = caseData.clientId || caseData.applicant?.id;
      if (caseData.applicant) {
          const clientFullName = caseData.applicant.fullName || `${caseData.applicant.nombre || ''} ${caseData.applicant.apellidos || ''}`.trim();
          
          // Vincular con el difunto de forma automática
          const currentClient = get().clients.find(c => c.id === clientId) as Partial<Client> | undefined;
          const associatedDeceasedIds = Array.from(new Set([...(currentClient?.associatedDeceasedIds || []), deceasedId])).filter(Boolean) as string[];

          const clientDoc = await addFirestoreClient({
              ...caseData.applicant,
              id: clientId, // Mantener ID si existe
              fullName: clientFullName || '',
              dni: (caseData.applicant as any).dniNumero || (caseData.applicant as any).dni || '',
              email: (caseData.applicant as any).email || '',
              phone: (caseData.applicant as any).phone || '',
              address: (caseData.applicant as any).domicilio || (caseData.applicant as any).address || '',
              associatedDeceasedIds
          }, funeralHomeId);
          clientId = clientDoc.id;
      }

      // Ahora vincular al difunto con el solicitante (vuelta) de forma segura
      if (deceasedId && clientId) {
          const currentDeceased = get().deceasedClients.find(d => d.id === deceasedId);
          const associatedApplicantIds = Array.from(new Set([...(currentDeceased?.associatedApplicantIds || []), clientId])).filter(Boolean) as string[];
          
          await addFirestoreDeceased({
              ...caseData.deceased,
              id: deceasedId,
              associatedApplicantIds
          }, funeralHomeId);
      }

      await addFirestoreCase({
          ...caseData,
          deceasedId,
          clientId,
          status: caseData.status || 'En Proceso'
      }, funeralHomeId);

      await get().fetchAllData();
      set((state) => { state.setIsLoading(false); }, false, 'data/saveCase/success');
    } catch (error: any) {
      set((state) => {
        state.setError("Error al guardar en la nube");
        state.setIsLoading(false);
      }, false, 'data/saveCase/error');
      throw error;
    }
  },

  saveClient: async (clientData) => {
    const funeralHomeId = get().currentUser?.funeralHomeId;
    if (!funeralHomeId) return;
    set((state) => { state.setIsLoading(true); }, false, 'data/saveClient/start');
    try {
      const normalizedData = {
        ...clientData,
        fullName: clientData.fullName || `${clientData.nombre || ''} ${clientData.apellidos || ''}`.trim() || ''
      };
      await addFirestoreClient(normalizedData, funeralHomeId);

      // Sincronización Bidireccional: Asegurar que todos los difuntos vinculados apunten a este cliente
      if (normalizedData.associatedDeceasedIds?.length) {
          for (const dId of normalizedData.associatedDeceasedIds) {
              const deceased = get().deceasedClients.find(d => d.id === dId);
              if (deceased && !deceased.associatedApplicantIds?.includes(normalizedData.id)) {
                  await addFirestoreDeceased({
                      ...deceased,
                      associatedApplicantIds: Array.from(new Set([...(deceased.associatedApplicantIds || []), normalizedData.id]))
                  }, funeralHomeId);
              }
          }
      }

      await get().fetchAllData();
      set((state) => { state.setIsLoading(false); }, false, 'data/saveClient/success');
    } catch (e) {
      set((state) => { state.setIsLoading(false); state.setError("Error al guardar cliente"); }, false, 'data/saveClient/error');
    }
  },

  saveDeceased: async (deceasedData) => {
    const funeralHomeId = get().currentUser?.funeralHomeId;
    if (!funeralHomeId) return;
    set((state) => { state.setIsLoading(true); }, false, 'data/saveDeceased/start');
    try {
      const normalizedData = {
        ...deceasedData,
        fullName: deceasedData.fullName || `${deceasedData.nombre || ''} ${deceasedData.apellidos || ''}`.trim() || ''
      };
      await addFirestoreDeceased(normalizedData, funeralHomeId);

      // Sincronización Bidireccional: Asegurar que todos los solicitantes vinculados apunten a este difunto
      if (normalizedData.associatedApplicantIds?.length) {
          for (const cId of normalizedData.associatedApplicantIds) {
              const client = get().clients.find(c => c.id === cId);
              if (client && !client.associatedDeceasedIds?.includes(normalizedData.id)) {
                  await addFirestoreClient({
                      ...client,
                      associatedDeceasedIds: Array.from(new Set([...(client.associatedDeceasedIds || []), normalizedData.id]))
                  }, funeralHomeId);
              }
          }
      }

      await get().fetchAllData();
      set((state) => { state.setIsLoading(false); }, false, 'data/saveDeceased/success');
    } catch (e) {
      set((state) => { state.setIsLoading(false); state.setError("Error al guardar difunto"); }, false, 'data/saveDeceased/error');
    }
  },

  saveSettings: async (settingsData: any) => {
    set((state) => { state.setIsLoading(true); }, false, 'data/saveSettings/start');
    try {
      const funeralHomeId = get().currentUser?.funeralHomeId;
      if (!funeralHomeId) throw new Error("No hay funeraria asociada");
      await updateFirestoreSettings(settingsData, funeralHomeId);
      await get().fetchAllData();
      set((state) => {
        state.setIsLoading(false);
      }, false, 'data/saveSettings/success');
    } catch (error: any) {
      set((state) => {
        state.setError("Error al guardar ajustes");
        state.setIsLoading(false);
      }, false, 'data/saveSettings/error');
      throw error;
    }
  },

  saveTemplate: async (template) => {
    set((state) => { state.setIsLoading(true); }, false, 'data/saveTemplate/start');
    try {
      const funeralHomeId = get().currentUser?.funeralHomeId;
      if (!funeralHomeId) throw new Error("No hay funeraria asociada");
      await addFirestoreTemplate(template, funeralHomeId);
      await get().fetchAllData();
      set((state) => { state.setIsLoading(false); }, false, 'data/saveTemplate/success');
    } catch (error: any) {
      set((state) => {
        state.setError("Error al guardar plantilla");
        state.setIsLoading(false);
      }, false, 'data/saveTemplate/error');
      throw error;
    }
  },

  deleteTemplate: async (templateId) => {
    set((state) => { state.setIsLoading(true); }, false, 'data/deleteTemplate/start');
    try {
      await deleteFirestoreTemplate(templateId);
      await get().fetchAllData();
      set((state) => { state.setIsLoading(false); }, false, 'data/deleteTemplate/success');
    } catch (error: any) {
      set((state) => {
        state.setError("Error al eliminar plantilla");
        state.setIsLoading(false);
      }, false, 'data/deleteTemplate/error');
      throw error;
    }
  },

  updateOnboardingStep: async (step) => {
    const user = get().currentUser;
    if (!user || !user.onboarding) return;

    const newSteps = { ...user.onboarding.steps, [step]: true };
    const allCompleted = Object.values(newSteps).every(v => v === true);
    
    const newOnboarding = {
      completed: allCompleted,
      steps: newSteps
    };

    set((state) => {
      if (state.currentUser) {
        state.currentUser.onboarding = newOnboarding;
      }
    }, false, 'data/updateOnboardingStep');

    try {
      await updateDoc(doc(db, "users", user.id), {
        onboarding: newOnboarding
      });
    } catch (error) {
      console.error("Error updating onboarding:", error);
    }
  },
  
  saveService: async (service) => {
    try {
      const funeralHomeId = get().currentUser?.funeralHomeId;
      if (!funeralHomeId) return;
      await addFirestoreService(service, funeralHomeId);
      await get().fetchAllData();
    } catch (e) {
      console.error("Error saving service:", e);
      alert("Error al guardar el servicio: " + e.message);
    }
  },

  deleteService: async (serviceId) => {
    await deleteFirestoreService(serviceId);
    await get().fetchAllData();
  },

  savePlan: async (plan) => {
    try {
      const funeralHomeId = get().currentUser?.funeralHomeId;
      if (!funeralHomeId) return;
      await addFirestorePlan(plan, funeralHomeId);
      await get().fetchAllData();
    } catch (e) {
      console.error("Error saving plan:", e);
      alert("Error al guardar el plan: " + e.message);
    }
  },

  deletePlan: async (planId) => {
    await deleteFirestorePlan(planId);
    await get().fetchAllData();
  },

  deleteClient: async (clientId) => {
    set((state) => { state.setIsLoading(true); }, false, 'data/deleteClient/start');
    try {
      await deleteFirestoreClient(clientId);
      await get().fetchAllData();
      set((state) => { state.setIsLoading(false); }, false, 'data/deleteClient/success');
    } catch (e) {
      set((state) => { state.setIsLoading(false); state.setError("Error al eliminar cliente"); }, false, 'data/deleteClient/error');
    }
  },

  deleteDeceased: async (deceasedId) => {
    set((state) => { state.setIsLoading(true); }, false, 'data/deleteDeceased/start');
    try {
      await deleteFirestoreDeceased(deceasedId);
      await get().fetchAllData();
      set((state) => { state.setIsLoading(false); }, false, 'data/deleteDeceased/success');
    } catch (e) {
      set((state) => { state.setIsLoading(false); state.setError("Error al eliminar difunto"); }, false, 'data/deleteDeceased/error');
    }
  },

  saveAppUser: async (user) => {
    const funeralHomeId = get().currentUser?.funeralHomeId;
    if (!funeralHomeId) return;
    await saveFirestoreUser({ ...user, funeralHomeId });
    await get().fetchAllData();
  },

  initializeTemplates: async () => {
    const funeralHomeId = get().currentUser?.funeralHomeId;
    if (!funeralHomeId) return;

    set((state) => { state.setIsLoading(true); }, false, 'data/initializeTemplates/start');
    
    try {
      const batch = writeBatch(db);
      const templates = getDefaultTemplates(funeralHomeId);
      
      templates.forEach(template => {
        const templateRef = doc(db, "templates", template.id);
        batch.set(templateRef, template);
      });
      
      await batch.commit();
      await get().fetchAllData();
      set((state) => { state.setIsLoading(false); }, false, 'data/initializeTemplates/success');
    } catch (error: any) {
      console.error("Error initializing templates:", error);
      set((state) => {
        state.setError("Error al inicializar plantillas");
        state.setIsLoading(false);
      }, false, 'data/initializeTemplates/error');
    }
  }
});
